import { ArrowDownToLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCreateRemittance, useFetchFinanceOverview } from '@/hooksApi/useRemittance';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import CustomModal from '@/components/Modal/Modal';
import { Flex } from 'antd';
import { Heading } from '@/components/Heading';
import { WarningModalIcon } from '@/components/Icons';
import { message } from 'antd';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';
import { formatWithThousandSeparator } from '@/utils';

const FinancePageHeader = () => {
  const { t } = useTranslation();
  const [modalWithdraw, setModalWithdraw] = useState(false);
  const { mutate, isLoading } = useCreateRemittance();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();

  // Fetch balance to disable withdraw when 0
  const { data: overviewData } = useFetchFinanceOverview(
    hasPermission(PermissionName.FINANCE),
  );
  const availableBalance = overviewData?.object?.available_cod ?? 0;

  return (
    <>
      <button
        type='button'
        onClick={() => setModalWithdraw(true)}
        disabled={!hasPermission(PermissionName.WITHDRAW_REMITTANCE) || availableBalance <= 0}
        className='flex items-center gap-2 h-9 px-4 rounded-[0.50rem] bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-opacity hover:bg-[rgb(230,217,60)]'
      >
        <ArrowDownToLine className='w-4 h-4' />
        {String(t('withdraw'))}
      </button>

      {modalWithdraw && (
        <CustomModal
          visible={modalWithdraw}
          onClose={() => setModalWithdraw(false)}
          title={String(t('withdraw'))}
          footer={false}
        >
          <Flex vertical>
            <Flex vertical className='items-center mt-8'>
              <WarningModalIcon />
              <Heading
                title={String(t('You_will_be_withdrawing_all_of_your_current_balance'))}
                extraClassName='my-4'
                level={5}
              />
              <span className='text-[20px] font-bold text-[rgb(57,57,60)] mb-6'>
                {formatWithThousandSeparator(availableBalance)} {String(t('iqd'))}
              </span>
            </Flex>
            <Flex className='justify-end w-full'>
              <button
                type='button'
                className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors whitespace-nowrap'
                disabled={isLoading}
                onClick={() =>
                  mutate(
                    {},
                    {
                      onSuccess: () => {
                        message.success(String(t('withdraw_success', { defaultValue: 'Withdrawal requested successfully' })));
                        queryClient.invalidateQueries(['finance-overview']);
                        queryClient.invalidateQueries(['availableCod']);
                        queryClient.invalidateQueries(['remittances']);
                        setModalWithdraw(false);
                      },
                      onError: () => {
                        message.error(String(t('operation_failed_please_try_again')));
                      },
                    },
                  )
                }
              >
                {isLoading && (
                  <span className='h-3.5 w-3.5 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
                )}
                {String(t('confirm'))}
              </button>
            </Flex>
          </Flex>
        </CustomModal>
      )}
    </>
  );
};

export default FinancePageHeader;
